import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useScroll, useSpring, useMotionValueEvent, useReducedMotion } from "framer-motion";
import { ArrowRight, Info } from "lucide-react";
import { PostureFigure } from "./PostureFigure";

const STAGES = [
  { title: "Slouched", points: "Head pushed forward · shoulders rounded · upper back curved", dot: "bg-brand-coral", on: "bg-brand-warmTint ring-brand-coral/40" },
  { title: "Realigning", points: "Chin gently back · shoulders opening · spine lengthening", dot: "bg-brand-sky", on: "bg-brand-skyTint ring-brand-sky/40" },
  { title: "Upright", points: "Ears over shoulders · shoulders over hips · natural spinal curves", dot: "bg-brand-green", on: "bg-brand-greenTint ring-brand-green/40" },
];

const clamp = (v) => Math.min(1, Math.max(0, v));

const Stages = ({ p }) => {
  const cur = p < 0.34 ? 0 : p < 0.67 ? 1 : 2;
  return (
    <ol className="grid gap-2">
      {STAGES.map((s, i) => (
        <li
          key={s.title}
          data-testid={`posture-stage-${i}`}
          data-active={i === cur ? "true" : "false"}
          className={`rounded-2xl px-4 py-2.5 ring-1 transition-[background-color,opacity] duration-300 sm:py-3 ${i === cur ? s.on : "bg-white/70 opacity-60 ring-slate-200"}`}
        >
          <p className="flex items-center gap-2 text-sm font-bold text-brand-ink sm:text-base">
            <span className={`h-2.5 w-2.5 rounded-full ${s.dot}`} aria-hidden="true" /> {s.title}
          </p>
          <p className={`mt-0.5 text-sm text-slate-600 ${i === cur ? "" : "hidden sm:block"}`}>{s.points}</p>
        </li>
      ))}
    </ol>
  );
};

const Inner = ({ p, controls }) => (
  <div className="container-x grid w-full items-center gap-4 pb-16 sm:gap-6 md:pb-0 lg:grid-cols-2 lg:gap-x-16">
    <div className="lg:col-start-1">
      <p className="eyebrow">Posture</p>
      <h2 id="posture-heading" className="font-heading text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl lg:text-4xl">
        Stand tall, <span className="text-gradient">spine aligned</span>
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-slate-600 sm:mt-4 sm:text-base md:text-lg [@media(max-height:700px)]:hidden">
        Watch an everyday slouch straighten up — head over shoulders, shoulders over hips.
      </p>
    </div>
    <div className="relative h-[34svh] max-h-[320px] min-h-[200px] lg:col-start-2 lg:row-span-2 lg:row-start-1 lg:h-[64vh] lg:max-h-[560px]">
      <span className="absolute left-0 top-0 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200" data-testid="posture-label">
        <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /> Posture illustration — not a treatment result
      </span>
      <PostureFigure p={p} className="h-full w-full" />
    </div>
    <div className="lg:col-start-1">
      {controls}
      <Stages p={p} />
      <div
        className="mt-4 h-2 overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-label="Posture progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(p * 100)}
        data-testid="posture-progress"
      >
        <div className="h-full rounded-full bg-gradient-to-r from-brand-coral via-brand-sky to-brand-green" style={{ width: `${Math.round(p * 100)}%` }} />
      </div>
      <Link to="/conditions#posture" className="text-link mt-4 text-sm [@media(max-height:700px)]:hidden" data-testid="posture-conditions-link">
        Read about posture-related pain <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  </div>
);

const SECTION_BG = "bg-gradient-to-b from-white via-brand-warmTint/70 to-white";

const PostureScrolly = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  const smooth = useSpring(scrollYProgress, { stiffness: 140, damping: 26, mass: 0.35 });
  const [p, setP] = useState(0);
  useMotionValueEvent(smooth, "change", (v) => setP(clamp((v - 0.08) / 0.8)));
  return (
    <section ref={ref} className={`relative h-[240vh] ${SECTION_BG}`} aria-labelledby="posture-heading" data-testid="posture-section">
      <div className="sticky top-16 flex h-[calc(100svh-4rem)] items-center overflow-hidden lg:top-20 lg:h-[calc(100svh-5rem)]">
        <Inner p={p} />
      </div>
    </section>
  );
};

const PostureStatic = () => {
  const [p, setP] = useState(0);
  const controls = (
    <div className="mb-4 flex gap-2" role="group" aria-label="Posture view">
      {[["Slouched", 0, "posture-toggle-slouched"], ["Upright", 1, "posture-toggle-upright"]].map(([label, v, id]) => (
        <button
          key={id}
          type="button"
          aria-pressed={p === v}
          onClick={() => setP(v)}
          data-testid={id}
          className={`rounded-full px-4 py-2 text-sm font-semibold ring-1 ${p === v ? "bg-brand-blue text-white ring-brand-blue" : "bg-white text-brand-ink ring-slate-200"}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
  return (
    <section className={`section ${SECTION_BG}`} aria-labelledby="posture-heading" data-testid="posture-section">
      <Inner p={p} controls={controls} />
    </section>
  );
};

export const PostureScroll = () => (useReducedMotion() ? <PostureStatic /> : <PostureScrolly />);
